import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { ComplianceSettingForm } from 'src/app/classes/form-groups/compliance-setting-form';
import { Company } from 'src/app/classes/models';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-compliance-company-background-checks',
  templateUrl: './compliance-company-background-checks.component.html',
  styleUrls: ['./compliance-company-background-checks.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceCompanyBackgroundChecksComponent implements OnInit {
  constructor(private ToastService: ToastService) {
    this.companyLiensMap.set('Not Required', null);
    this.companyLiensMap.set('0 - $5,000', 5000);

    const increment = 10000;
    const maxValue = 1000000;

    for (let i = increment; i <= maxValue; i += increment) {
      const key = `< $${i.toLocaleString()}`;
      this.companyLiensMap.set(key, i);
    }
  }

  @Input() complianceSettingForms?: ComplianceSettingForm[];
  @Input() Company!: Company;

  // Used solely for Canadian business verification.
  // A value of 30 for corporateIdentity on a given setting equates
  // to true, 0 is false.
  CanadianComplianceSettingsMap = new Map<
    ComplianceSettingForm,
    { businessVerification: boolean }
  >([]);

  collapseCompanyBackgroundChecks: boolean = true;

  rowHeaders: string[] = [
    '',
    'Corporation In Good Standing',
    'Corporate Identity Check',
    'Corporate Watchlist',
    'Corporate Bankruptcy Search',
    'Corporate Liens & Judgements',
    'W9 Check'
  ];

  corporateIdentityMap: Map<string, number | null> = new Map([
    ['Not Required', null],
    ['0', 0],
    ['10', 10],
    ['20', 20],
    ['30', 30],
    ['40', 40],
    ['50', 50]
  ]);

  ownerWatchlistMap: Map<string, number | null> = new Map([
    ['Not Required', null],
    ['No Watchlists', 0],
    ['1 Watchlist', 1],
    ['Multiple Watchlists', 2]
  ]);

  corporateBankruptcySearchMap: Map<string, number | null> = new Map([
    ['Not Required', null],
    ['Not Allowed', 0],
    ['1 Year', 1],
    ['2 Years', 2],
    ['3 Years', 3],
    ['4 Years', 4],
    ['5 Years', 5],
    ['6 Years', 6],
    ['7 Years', 7]
  ]);

  companyLiensMap: Map<string, number | null> = new Map();

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  canadianBusinessVerificationCheck(
    ComplianceSettingForm: ComplianceSettingForm,
    businessVerification: boolean
  ) {
    if (businessVerification) {
      ComplianceSettingForm.patchValue({ companyIdentity: 30 });
    } else {
      ComplianceSettingForm.patchValue({ companyIdentity: null });
    }
  }

  getComplianceSettingForm(ComplianceSettingForm: ComplianceSettingForm) {
    return this.CanadianComplianceSettingsMap.get(ComplianceSettingForm);
  }

  initializeCanadianComplianceSettingsMap(
    ComplianceSettingForms: ComplianceSettingForm[]
  ) {
    ComplianceSettingForms.forEach((ComplianceSettingForm) => {
      const {
        value: { companyIdentity }
      } = ComplianceSettingForm;
      this.CanadianComplianceSettingsMap.set(ComplianceSettingForm, {
        businessVerification: companyIdentity ? companyIdentity >= 30 : false
      });
    });
  }

  ngOnChanges(SimpleChanges: SimpleChanges) {
    const { complianceSettingForms } = SimpleChanges;

    if (complianceSettingForms) {
      const { currentValue } = complianceSettingForms;
      this.initializeCanadianComplianceSettingsMap(currentValue);
    }
  }

  ngOnInit(): void {
    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client information.',
        { returnError: true }
      );
    }

    if (!this.complianceSettingForms) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing compliance setting forms.',
        { returnError: true }
      );
    }

    this.initializeCanadianComplianceSettingsMap(this.complianceSettingForms);
  }
}
